package com.kanati.admin.features.inventory

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.shared.commerce.AdjustInventoryRequest
import com.kanati.shared.commerce.InventoryResponse
import com.kanati.shared.commerce.SetInventoryRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private data class QuantityDialogState(
    val productId: String,
    val productName: String,
    val quantityOnHand: Int,
)

@Composable
fun InventoryScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
) {
    val scope = rememberCoroutineScope()
    var inventory by remember { mutableStateOf<List<InventoryResponse>>(emptyList()) }
    var isRefreshing by remember { mutableStateOf(false) }
    var pendingProductId by remember { mutableStateOf<String?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var updateError by remember { mutableStateOf<String?>(null) }
    var updateSuccess by remember { mutableStateOf<String?>(null) }
    var dialogError by remember { mutableStateOf<String?>(null) }
    var quantityDialog by remember { mutableStateOf<QuantityDialogState?>(null) }
    var quantityDraft by remember { mutableStateOf("") }
    var searchQuery by remember { mutableStateOf("") }

    val visibleInventory = inventory.let { items ->
        val q = searchQuery.trim().lowercase()
        if (q.isBlank()) items
        else items.filter { it.productName.lowercase().contains(q) }
    }

    fun missingDistrictMessage(): String =
        when {
            districtLoading -> "Loading district context..."
            districtLoadError != null -> "District context could not be loaded. Refresh context and select a district."
            district == null -> "Select a district to view inventory."
            else -> "Select a school to view inventory."
        }

    fun mergeInventoryItem(updatedItem: InventoryResponse) {
        var didReplace = false
        inventory = inventory.map { existing ->
            if (existing.productId == updatedItem.productId) {
                didReplace = true
                updatedItem
            } else {
                existing
            }
        }.let { items ->
            if (didReplace) items else (items + updatedItem)
        }
    }

    suspend fun refreshInventory(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isRefreshing = true
        loadError = null
        try {
            inventory = apiClient.getInventory(selectedDistrict.id, selectedSchool.id)
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load inventory."
        } finally {
            isRefreshing = false
        }
    }

    fun loadInventory() {
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict == null || selectedSchool == null) {
            inventory = emptyList()
            loadError = missingDistrictMessage()
            return
        }

        scope.launch {
            refreshInventory(selectedDistrict, selectedSchool)
        }
    }

    fun adjustQuantity(item: InventoryResponse, delta: Int) {
        val selectedDistrict = district ?: return
        val selectedSchool = school ?: return
        val optimisticItem = item.copy(quantityOnHand = item.quantityOnHand + delta)
        pendingProductId = item.productId
        updateError = null
        updateSuccess = null
        dialogError = null
        mergeInventoryItem(optimisticItem)
        if (quantityDialog?.productId == item.productId) {
            quantityDialog = quantityDialog?.copy(quantityOnHand = optimisticItem.quantityOnHand)
            quantityDraft = optimisticItem.quantityOnHand.toString()
        }

        scope.launch {
            try {
                val updatedItem = apiClient.adjustInventory(
                    AdjustInventoryRequest(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        productId = item.productId,
                        delta = delta,
                        reason = if (delta > 0) "quick_increment" else "quick_decrement",
                    )
                )
                mergeInventoryItem(updatedItem)
                quantityDialog = quantityDialog
                    ?.takeIf { it.productId != item.productId }
                    ?: QuantityDialogState(
                        productId = updatedItem.productId,
                        productName = updatedItem.productName,
                        quantityOnHand = updatedItem.quantityOnHand,
                    ).takeIf { quantityDialog?.productId == item.productId }
                if (quantityDialog?.productId == item.productId) {
                    quantityDraft = updatedItem.quantityOnHand.toString()
                }
            } catch (exception: Exception) {
                mergeInventoryItem(item)
                if (quantityDialog?.productId == item.productId) {
                    quantityDialog = quantityDialog?.copy(quantityOnHand = item.quantityOnHand)
                    quantityDraft = item.quantityOnHand.toString()
                }
                updateError = exception.message ?: "Unable to update inventory."
            } finally {
                pendingProductId = null
            }
        }
    }

    fun openQuantityDialog(item: InventoryResponse) {
        quantityDialog = QuantityDialogState(
            productId = item.productId,
            productName = item.productName,
            quantityOnHand = item.quantityOnHand,
        )
        quantityDraft = item.quantityOnHand.toString()
        dialogError = null
        updateError = null
    }

    fun applyExactQuantity() {
        val selectedDistrict = district
        val selectedSchool = school
        val activeDialog = quantityDialog
        val quantityOnHand = quantityDraft.trim().toIntOrNull()

        when {
            selectedDistrict == null || selectedSchool == null -> dialogError = "Select a school before setting inventory."
            activeDialog == null -> dialogError = "Choose an inventory row first."
            quantityOnHand == null -> dialogError = "Enter a whole number quantity."
            quantityOnHand < 0 -> dialogError = "Quantity cannot be negative."
            else -> {
                pendingProductId = activeDialog.productId
                dialogError = null
                updateError = null
                scope.launch {
                    try {
                        val updatedItem = apiClient.setInventory(
                            SetInventoryRequest(
                                districtId = selectedDistrict.id,
                                schoolId = selectedSchool.id,
                                productId = activeDialog.productId,
                                quantityOnHand = quantityOnHand,
                                reason = "operator_set_quantity",
                            )
                        )
                        mergeInventoryItem(updatedItem)
                        quantityDialog = null
                        quantityDraft = ""
                        updateSuccess = "\u2713 ${activeDialog.productName} set to $quantityOnHand."
                    } catch (exception: Exception) {
                        dialogError = exception.message ?: "Unable to set inventory quantity."
                    } finally {
                        pendingProductId = null
                    }
                }
            }
        }
    }

    LaunchedEffect(district?.id, school?.id, districtLoading, districtLoadError) {
        pendingProductId = null
        quantityDialog = null
        quantityDraft = ""
        updateError = null
        updateSuccess = null
        dialogError = null
        searchQuery = ""

        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict != null && selectedSchool != null) {
            refreshInventory(selectedDistrict, selectedSchool)
        } else {
            inventory = emptyList()
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Inventory",
        theme = theme,
        description = "Use \u00b1 buttons for quick one-step adjustments in ${school?.name ?: "the selected school"}.",
        headerActions = {
            ActionButton(
                label = if (isRefreshing) "Loading..." else "Refresh",
                onClick = ::loadInventory,
                theme = theme,
                isPrimary = false,
                isDisabled = isRefreshing || pendingProductId != null || district == null || school == null,
            )
        },
    ) {
        if (!canEdit) {
            StatusBanner(
                message = "Read-only access. Inventory changes require edit permission for this school.",
                theme = theme,
            )
        }
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                    flex(1)
                }
            }
        ) {
            TextField(
                theme = theme,
                label = "Search",
                value = searchQuery,
                placeholder = "Product name\u2026",
                onValueChange = { searchQuery = it },
            )
        }

        loadError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
        }

        updateError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        updateSuccess?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Success,
            )
        }

        if (visibleInventory.isEmpty() && !isRefreshing && loadError == null) {
            val emptyMessage = if (searchQuery.isNotBlank())
                "No products matched \u201c${searchQuery.trim()}\u201d."
            else
                "No inventory found for ${district?.name ?: "the selected district"}."
            StatusBanner(
                message = emptyMessage,
                theme = theme,
            )
        }

        visibleInventory.forEach { item ->
            val isUpdatingRow = pendingProductId == item.productId

            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "16px")
                        property("padding", "16px 18px")
                        property("border-radius", "22px")
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "0 14px 28px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "4px")
                            property("min-width", "220px")
                            property("flex", "1")
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("font-weight", "650")
                            }
                        }
                    ) {
                        Text(item.productName)
                    }
                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("color", theme.onSurfaceVariant)
                                property("font-size", "0.9rem")
                            }
                        }
                    ) {
                        Text(item.updatedAt ?: "Never updated")
                    }
                }

                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "10px")
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    QuantityStepButton(
                        label = "-",
                        theme = theme,
                        isDisabled = isUpdatingRow || item.quantityOnHand <= 0 || district == null,
                        onClick = { adjustQuantity(item, -1) },
                    )

                    QuantityDisplayButton(
                        quantity = item.quantityOnHand,
                        theme = theme,
                        isDisabled = isUpdatingRow || district == null,
                        onClick = { openQuantityDialog(item) },
                    )

                    QuantityStepButton(
                        label = "+",
                        theme = theme,
                        isDisabled = isUpdatingRow || district == null,
                        onClick = { adjustQuantity(item, 1) },
                    )
                }
            }
        }
    }

    quantityDialog?.let { dialog ->
        QuantitySetDialog(
            productName = dialog.productName,
            quantityDraft = quantityDraft,
            onQuantityDraftChange = { quantityDraft = it },
            onCancel = {
                if (pendingProductId != dialog.productId) {
                    quantityDialog = null
                    quantityDraft = ""
                    dialogError = null
                }
            },
            onApply = ::applyExactQuantity,
            theme = theme,
            isApplying = pendingProductId == dialog.productId,
            error = dialogError,
        )
    }
}

@Composable
private fun QuantityStepButton(
    label: String,
    theme: MaterialThemePalette,
    isDisabled: Boolean,
    onClick: () -> Unit,
) {
    var isPressed by remember(isDisabled) { mutableStateOf(false) }

    Button(
        attrs = {
            if (isDisabled) disabled()
            onClick { onClick() }
            onMouseDown { if (!isDisabled) isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { if (!isDisabled) isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(0.px)
                property("width", "44px")
                property("height", "44px")
                property("border-radius", "999px")
                property("border", "1px solid ${theme.outlineVariant}")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceContainer} 100%)"
                )
                property("color", theme.onSurface)
                property("font-size", "1.2rem")
                property("font-weight", "700")
                property(
                    "box-shadow",
                    if (isPressed && !isDisabled) {
                        "0 4px 10px ${theme.shadow}, inset 0 2px 4px ${theme.scrim}"
                    } else {
                        "0 10px 22px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}"
                    }
                )
                property(
                    "transform",
                    if (isPressed && !isDisabled) "translateY(2px) scale(0.98)" else "translateY(0) scale(1)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease")
                property("cursor", if (isDisabled) "default" else "pointer")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
                if (isDisabled) {
                    property("opacity", "0.5")
                }
            }
        }
    ) {
        Text(label)
    }
}

@Composable
private fun QuantityDisplayButton(
    quantity: Int,
    theme: MaterialThemePalette,
    isDisabled: Boolean,
    onClick: () -> Unit,
) {
    var isPressed by remember(isDisabled) { mutableStateOf(false) }

    Button(
        attrs = {
            if (isDisabled) disabled()
            onClick { onClick() }
            onMouseDown { if (!isDisabled) isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { if (!isDisabled) isPressed = true }
            onTouchEnd { isPressed = false }
            onTouchCancel { isPressed = false }
            onBlur { isPressed = false }
            style {
                padding(10.px, 16.px)
                borderRadius(999.px)
                property("min-width", "104px")
                property("border", "1px solid ${theme.outlineVariant}")
                property("background", theme.secondaryContainer)
                property("color", theme.onSecondaryContainer)
                property("font-weight", "700")
                property("letter-spacing", "-0.01em")
                property(
                    "box-shadow",
                    if (isPressed && !isDisabled) {
                        "0 4px 10px ${theme.shadow}, inset 0 2px 4px ${theme.scrim}"
                    } else {
                        "0 12px 24px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}"
                    }
                )
                property(
                    "transform",
                    if (isPressed && !isDisabled) "translateY(2px) scale(0.985)" else "translateY(0) scale(1)"
                )
                property("transition", "transform 120ms ease, box-shadow 120ms ease, opacity 120ms ease")
                property("cursor", if (isDisabled) "default" else "pointer")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
                if (isDisabled) {
                    property("opacity", "0.6")
                }
            }
        }
    ) {
        Text("Qty $quantity")
    }
}

@Composable
private fun QuantitySetDialog(
    productName: String,
    quantityDraft: String,
    onQuantityDraftChange: (String) -> Unit,
    onCancel: () -> Unit,
    onApply: () -> Unit,
    theme: MaterialThemePalette,
    isApplying: Boolean,
    error: String?,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "24px")
                property("background", "rgba(15, 23, 42, 0.24)")
                property("backdrop-filter", "blur(10px)")
                property("z-index", "20")
            }
            onClick { onCancel() }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("width", "min(100%, 420px)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "16px")
                    padding(22.px)
                    borderRadius(28.px)
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                    )
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 24px 60px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                }
            }
        ) {
            H3(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                        property("font-size", "1.4rem")
                        property("letter-spacing", "-0.03em")
                    }
                }
            ) {
                Text("Set Quantity")
            }

            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("line-height", "1.5")
                    }
                }
            ) {
                Text("Set the exact on-hand quantity for $productName.")
            }

            error?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "8px")
                    }
                }
            ) {
                P(
                    attrs = {
                        style {
                            margin(0.px)
                            property("color", theme.onSurfaceVariant)
                            property("font-size", "0.8rem")
                            property("font-weight", "700")
                            property("letter-spacing", "0.08em")
                            property("text-transform", "uppercase")
                        }
                    }
                ) {
                    Text("Quantity")
                }

                Input(type = InputType.Number) {
                    value(quantityDraft)
                    onInput { onQuantityDraftChange(it.value?.toString() ?: "") }
                    style {
                        padding(14.px, 16.px)
                        borderRadius(18.px)
                        property("box-sizing", "border-box")
                        property("width", "100%")
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("color", theme.onSurface)
                        property("border", "1px solid ${theme.outline}")
                        property("box-shadow", "inset 0 1px 0 ${theme.scrim}, 0 12px 24px ${theme.shadow}")
                        property("font-size", "1rem")
                        property("font-weight", "600")
                    }
                }
            }

            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("justify-content", "flex-end")
                        property("gap", "10px")
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                ActionButton(
                    label = "Cancel",
                    onClick = onCancel,
                    theme = theme,
                    isPrimary = false,
                    isDisabled = isApplying,
                )
                ActionButton(
                    label = if (isApplying) "Applying..." else "Apply",
                    onClick = onApply,
                    theme = theme,
                    isDisabled = isApplying,
                )
            }
        }
    }
}
